import {
  Breadcrumb,
  Button,
  Controller,
  DownloadExcel,
  ErrorAlert,
  ErrorMessage,
  Input,
  LinkIcon,
  Modal,
  modalHandler,
  ModalOpenBtn,
  PageHero,
  SearchToggle,
  Select,
  success,
  SwitchInput,
  TableRowsPerPage,
  useCallback,
  useCustomForm,
  useDelete,
  useEffect,
  useMemo,
  useRowSelection,
  useState,
  useToggle,
  formatNumber,
  ArrowPathIcon
} from "@";
import { DataTable } from "@/shared/components/tanstackTable";
import { getLinks, linkToggleActive, linkToggleArchive } from "../api/api";
import { useSaveLinkMutation } from "../api/mutations";
import { getUsers } from "../../users/utils/api";
import MultiSearch from "../components/MultiSearch";
import MultiActions from "../components/MultiActions";
import { defaultValues, schema } from "../lib/validation";
import { excelHeaderCellsName } from "../lib/options";
import { TableActions, tableColumns } from "../components/TableOptions";
import { useLinksPage } from "../hooks";

const Page = () => {
  // Breadcrumb links
  const links = [
    {
      name: "الروابط",
      link: "",
    },
  ];

  // State variables
  const [isEdit, setIsEdit] = useState(false);
  const { selectedIDs, handleSelect } = useRowSelection();
  const [affiliateUsers, setAffiliateUsers] = useState([]);

  // List page hook — URL-based search + pagination, backed by React Query
  const {
    data,
    setData,
    isLoading,
    error,
    pg,
    updatePg,
    search,
    updateSearch,
    handleSearchSubmit,
    handleSearchReset,
    refetch,
    linksAggregation,
  } = useLinksPage();
  const { deleteHandler } = useDelete(data, setData, "affiliateLink");
  const saveLinkMutation = useSaveLinkMutation();

  // Toggle active and archive states
  const { active, archive } = useToggle(
    setData,
    linkToggleActive,
    linkToggleArchive,
  );

  // Add custom row for totals
  const dataWithCustomRowForTotals = useMemo(
    () => [
      ...data,
      {
        id: "customRow",
        affiliateName: "المجموع",
        numberOfVisits: linksAggregation?.totalNumberOfVisits,
        totalDonationAmount: linksAggregation?.totalDonationAmount,
        numberOfDonations: linksAggregation?.totalNumberOfDonations,
      },
    ],
    [
      data,
      linksAggregation?.totalNumberOfVisits,
      linksAggregation?.totalDonationAmount,
      linksAggregation?.totalNumberOfDonations,
    ],
  );

  // Custom form hooks
  const {
    reset,
    watch,
    errors,
    control,
    register,
    setValue,
    getValues,
    handleSubmit,
    isSubmitting,
  } = useCustomForm(defaultValues, schema);

  // Table columns configuration with actions
  const columns = useMemo(
    () => [
      ...tableColumns,
      {
        id: "actions",
        header: "خيارات",
        size: 200,
        cell: ({ row }) =>
          row.original.id === "customRow" ? null : (
            <TableActions
              row={row.original}
              key={row.original.id}
              reset={reset}
              setIsEdit={setIsEdit}
              ToggleActive={active}
              ToggleArchive={archive}
              deleteHandler={deleteHandler}
            />
          ),
      },
    ],
    [reset, active, archive, deleteHandler],
  );

  // Clear form
  const clearForm = () => {
    setIsEdit(false);
    reset(defaultValues);
  };

  // Save form data
  const saveFormData = async (from) => {
    try {
      await saveLinkMutation.mutateAsync(from);
      await success("تم الحفظ بنجاح");
      modalHandler();
      clearForm();
    } catch (error) {}
  };

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      const { data: usersRes } = await getUsers();
      setAffiliateUsers(usersRes?.data);
    } catch (error) {}
  }, []);

  // Generate options for the affiliate users select input
  const affiliateUsersOptions = useMemo(
    () =>
      affiliateUsers?.map((option) => ({
        value: option.id,
        label: option.name,
      })),
    [affiliateUsers],
  );

  useEffect(() => {
    fetchUsers();
    // updateSearch({ show: true });
  }, []);

  const hasSelection = selectedIDs.length > 0;


  return (
    <>
      {/* Error alert */}
      <ErrorAlert reload={refetch} show={error} />

      {/* Breadcrumb */}
      <Breadcrumb links={links} />

      {/* Modal for adding/editing links */}
      <Modal close={clearForm} title={isEdit ? "تعديل" : "اضافة"}>
        <form
          className="grid grid-cols-1 gap-5 p-2 md:p-5"
          onSubmit={handleSubmit(saveFormData)}
        >
          {/* Input for the title */}
          <Input
            label="العنوان"
            error={errors.title}
            input={register("title")}
          />

          {/* Input for the query value */}
          {!isEdit && (
            <Input
              disabled={isEdit}
              label="كلمة دلالية مخصصة"
              error={errors.queryValue}
              input={register("queryValue")}
              value={isEdit ? getValues("queryValue") : null}
            />
          )}

          {/* Select input for the affiliate ID */}
          {!isEdit && (
            <div>
              <label className="input-label">
                المعلن
                <span className="star">*</span>
              </label>

              <Controller
                name="affiliateId"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder=""
                    options={affiliateUsersOptions}
                    isClearable
                    isRtl
                    value={affiliateUsersOptions.find(
                      (option) => option.value === watch("affiliateId"),
                    )}
                    isSearchable
                    onChange={(affiliateUsersOptions) =>
                      field.onChange(affiliateUsersOptions?.value || "")
                    }
                  />
                )}
              />
              <ErrorMessage value={errors.affiliateId?.message} />
            </div>
          )}

          {/* Checkbox for creating copies of the link */}
          {!isEdit && (
            <div>
              <SwitchInput
                label="تكرار أضافة الرابط"
                checked={watch("createCopies")}
                change={(value) => setValue("createCopies", value)}
              />

              {/* Input for the number of copies */}
              {watch("createCopies") && (
                <Input
                  min={0}
                  type="number"
                  label="عدد النسخ"
                  error={errors.numberOfCopies}
                  input={register("numberOfCopies")}
                />
              )}
            </div>
          )}

          {/* Button to submit the form */}
          <Button isLoading={isSubmitting} type="submit" title="حفظ البيانات" />
        </form>
      </Modal>

      {/* Main container */}
      <main className="table-card relative">
        <PageHero
          eyebrow="التسويق / الروابط"
          title="الروابط"
          count={data?.length}
          icon={LinkIcon}
          hideCount={isLoading}
        >
          <TableRowsPerPage
            pagination={pg}
            rowsPerPageOptions={[5, 10, 50, 100, 300, 500, 1000]}
            onRowsPerPageChange={(size) => updatePg({ size: size })}
          />

        
          {/* Download excel */}
          <DownloadExcel
            fetch={getLinks}
            length={data?.length}
            search={search.values}
            headerCellsName={excelHeaderCellsName}
          />

          {/* Create link */}
          <ModalOpenBtn style="btn-green" title="إضافة " />

          {/* Search toggle */}
          <SearchToggle toggle={() => updateSearch({ show: !search.show })} />
        </PageHero>

        {/* Multi Search inputs */}
        <MultiSearch
          search={search}
          onSubmit={handleSearchSubmit}
          linksLength={data?.length}
          updateSearch={updateSearch}
          affiliateUsers={affiliateUsers}
          onReset={handleSearchReset}
        />

         {/* Action toolbar */}
         <div className="relative mt-4 rounded-2xl border border-gray-100 bg-gradient-to-l from-purple-50/30 via-white to-white p-2 dark:border-gray-700/60 dark:from-purple-950/20 dark:via-gray-800 dark:to-gray-800 md:mt-6">
          {/* Selection state badge — own row on mobile */}
          {hasSelection && (
            <div className="mb-2 flex items-center gap-2 px-1 lg:mb-0 lg:hidden">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-100 px-2.5 py-1 text-xs font-bold tabular-nums text-purple-900 ring-1 ring-purple-200 dark:bg-purple-950/50 dark:text-purple-300 dark:ring-purple-800/40">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />
                {formatNumber(selectedIDs.length)} محدد
              </span>
            </div>
          )}

          {/* Buttons grid — 2 cols on mobile, inline on lg+ */}
          <div className="grid grid-cols-2 gap-2 lg:flex lg:flex-wrap lg:items-center [&>*]:!w-full lg:[&>*]:!w-fit">
            {/* Inline selection badge for lg+ */}
            {hasSelection && (
              <span className="!hidden lg:me-1 lg:!inline-flex lg:items-center lg:gap-1.5 lg:rounded-full lg:bg-purple-100 lg:px-2.5 lg:py-1 lg:text-xs lg:font-bold lg:tabular-nums lg:text-purple-900 lg:ring-1 lg:ring-purple-200 dark:lg:bg-purple-950/50 dark:lg:text-purple-300 dark:lg:ring-purple-800/40">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />
                {formatNumber(selectedIDs.length)} محدد
              </span>
            )}

              {/* Multi actions */}
          <MultiActions submitted={refetch} selectedIDs={selectedIDs} />


            {/* Refresh — full row on mobile, pushed to end on lg */}
            <button
              onClick={refetch}
              className="btn-gust col-span-2 inline-flex items-center justify-center gap-2 text-base lg:col-span-1 lg:ms-auto lg:!w-fit"
              title="تحديث"
            >
              <ArrowPathIcon className="h-5 w-5 transition-transform duration-500 hover:-rotate-180" />
              تحديث
            </button>
          </div>
        </div>

        {/* Table */}
        <DataTable
          data={dataWithCustomRowForTotals}
          columns={columns}
          isLoading={isLoading}
          pagination={pg}
          onPageChange={(page) => updatePg({ page })}
          onPageSizeChange={(size) => updatePg({ size, page: 1 })}
          selectable
          onSelectedChange={handleSelect}
        />
      </main>
    </>
  );
};

export default Page;
